/**
 * Note(Erfan):
 * Previously, we used a regular text field along with the "react-mentions" library to store mentions in a
 * standard format, using "@(name)[ID]"" syntax. However, after transitioning to "react-quill" and "quill-mentions",
 * the storage format for mentions has changed to some HTML values. Unfortunately, other parts of our application
 * still rely on the "@(name)[ID]"" format. To address this, we needed to develop converters that enable the conversion
 * between the storage format used by quill-mentions and our standard format. These converters allow us to maintain
 * mentions in the familiar format, while internally, within the Editor, they are manipulated using the HTML
 * format employed by quill-mentions.
 *
 * P.S: Ideally we shouldn't care about the format we're saving mentions, there should be no need to extract mentions
 * out of the text. We need to stores mentions separately in the comment row. However, it's not like that at the moment.
 */
import { find } from 'linkifyjs'
import { convertHtmlWithMentionsToStandardFormat, quillVariableObjectRegex } from '@omnea-app/requests/requestsFrontendClient'

// Pasted images are persisted by reference only. Strip the transient
// `src` the editor used for live preview (a blob:/signed URL), keeping the
// `data-filestore-id` so the image can be resolved again at render time.
// Scoped to filestore-backed images so any other `<img src>` is left untouched.
export const stripImageSrc = (html: string) =>
  html.replace(/(<img\b(?=[^>]*\bdata-filestore-id="[^"]+")[^>]*?)\s+src="[^"]*"/gi, '$1')

// Remove any in-flight upload placeholders so they never persist — a pasted image
// is either swapped in once uploaded or dropped on failure.
export const stripImageUploadPlaceholders = (html: string) =>
  html.replace(/<span\b[^>]*\bql-image-upload-placeholder\b[^>]*>[\s\S]*?<\/span>/gi, '')

// True while a pasted image is still uploading (its placeholder span is in the
// HTML). Used to block submit so an in-flight image isn't silently stripped on
// save. Matched as a class on a tag so the literal text typed in a comment
// doesn't falsely block submission.
export const hasImageUploadPlaceholders = (html: string): boolean => /<[^>]*\bql-image-upload-placeholder\b/i.test(html)

// The filestore ids of every referenced image in a rich-text value.
export const getImageFilestoreIds = (html: string): string[] => {
  const ids: string[] = []
  const regex = /<img\b[^>]*\bdata-filestore-id="([^"]+)"/gi
  let match: RegExpExecArray | null
  while ((match = regex.exec(html)) !== null) {
    if (match[1]) {
      ids.push(match[1])
    }
  }
  return Array.from(new Set(ids))
}

// Inject resolved signed URLs back into image references so they display in the
// editor when editing existing content (the reference is kept; `src` is
// stripped again on save).
export const injectImageSrcs = (html: string, urlByFilestoreId: Record<string, string | null>): string =>
  html.replace(/(<img\b[^>]*\bdata-filestore-id="([^"]+)"[^>]*?)>/gi, (match, openTag: string, id: string) => {
    const url = urlByFilestoreId[id]
    return url ? `${openTag} src="${url}">` : match
  })

export const formatCommentHtmlTextToStandardFormat = (html: string) =>
  removeNewLinesFromEnd(convertHtmlWithMentionsToStandardFormat(stripImageSrc(stripImageUploadPlaceholders(html))))

// @(name)[any text]
const quillVariableUserIdRegex = /@\[([^\[]+)(?:\s\[([^\]]+)\])?\]\(([^)]+)\)/g

export function convertStandardFormatToHtmlWithMentions(inputString: string) {
  if (inputString === '') {
    return inputString
  }

  return inputString
    .replace(quillVariableObjectRegex, (_, value, ID) => {
      const hasError = ID.includes('isValid: false')

      if (hasError) {
        return `<span class="mention" data-index="0" data-denotation-char="@" data-error="${hasError ? 'true' : 'false'}" data-id="${ID}" data-value="${value}">﻿<span contenteditable="false">${value}</span>﻿</span>`
      }

      return `<span class="mention" data-index="0" data-denotation-char="@" data-id="${ID}" data-value="${value}">﻿<span contenteditable="false">${value}</span>﻿</span>`
    })
    .replace(quillVariableUserIdRegex, (_, fullName, email, userId) => {
      const userName = email ? `${fullName} [${email}]` : fullName
      return `<span class="mention" data-index="0" data-denotation-char="@" data-id="${userId}" data-value="${userName}">﻿<span contenteditable="false">${userName}</span>﻿</span>`
    })
}

export function removeNewLinesFromEnd(htmlString: string) {
  const regex = /<p><br><\/p>\s*$/
  while (regex.test(htmlString)) {
    htmlString = htmlString.replace(regex, '')
  }
  return htmlString
}

export function isHtmlTextEmpty(htmlString: string) {
  return removeNewLinesFromEnd(htmlString).trim().length === 0
}

export const isPastedUrl = (text: string) => {
  const trimmed = text.trim()
  const matches = find(trimmed)
  return matches.length === 1 && matches[0]?.value === trimmed && matches[0]?.type === 'url'
}

// Note: prepend 'http' to link in case user provided link doesn't have it (https://github.com/zenoamaro/react-quill/issues/529)
export const sanitizeUrl = (url: string) => {
  const VALID_URL_PREFIXES = ['http://', 'https://', 'mailto:', 'ftp://', 'tel:']
  const lower = url.toLowerCase()
  if (VALID_URL_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return url
  }
  return `http://${url}`
}
